import { useMutation } from "@apollo/client/react";
import {
  useCurrentUser,
  usePremiumFeature,
  RoleProvider,
  useTeam,
} from "@hooks";
import { useRole } from "@hooks/use-role";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useNavigate } from "react-router";

import { AccountItemLine } from "./AccountItem";
import EDIT_USER from "../../graphql/mutations/EditUser";
import { alert } from "../common/Alert";
import {
  DSButton,
  DSDropdown,
  DSInput,
  DSLinkButton,
} from "../design-system/molecules";
import { mapRoleIdToRole } from "../team/TeamManagement/utils";

export default function ProfileSettings() {
  const navigate = useNavigate();

  const { user } = useCurrentUser();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const { releaseWebEnableFtueTour } = useFlags();

  const shouldShowChangeEmail = hasFeatureAccess(
    UserPermissions.CHANGE_EMAIL_ENABLED,
  );

  return (
    <div className="flex-1 flex flex-col gap-md">
      <div className="flex-1 divide-y divide-secondary">
        <AccountItemLine title="Name:" dataTestId="profile-name-line">
          <ProfileNameSection user={user} />
        </AccountItemLine>
        <AccountItemLine
          title="Email:"
          dataTestId="profile-email-line"
        >
          <Tooltip
            arrow
            placement="right"
            title={
              user?.isConfirmed
                ? "You have successfully confirmed your email."
                : "You have not confirmed your email yet."
            }
          >
            <div className="flex flex-row items-center gap-sm">
              <span className="text-primary">{user?.email}</span>
              <div className="flex flex-row items-center gap-sm">
                {user?.isConfirmed ? (
                  <span className="text-success text-xs">✓</span>
                ) : (
                  <span className="text-danger text-xs">✗</span>
                )}
                {shouldShowChangeEmail && (
                  <DSLinkButton
                    dataTestId="manage-email-button"
                    onClick={() =>
                      navigate("/registration_confirmation")
                    }
                  >
                    Manage Email
                  </DSLinkButton>
                )}
              </div>
            </div>
          </Tooltip>
        </AccountItemLine>
        {releaseWebEnableFtueTour && (
          <AccountItemLine
            title="Role:"
            dataTestId="profile-role-line"
          >
            <RoleProvider>
              <ProfileRoleSection />
            </RoleProvider>
          </AccountItemLine>
        )}
        <AccountItemLine
          title="User ID:"
          dataTestId="profile-user-id-line"
        >
          <span className="text-primary">{user?.uuid}</span>
        </AccountItemLine>
      </div>
    </div>
  );
}

const ROLE_LABELS = {
  DVM: "Veterinarian",
  TECHNICIAN: "Vet Tech",
  PRACTICE_MANAGER: "Practice Manager",
  OTHER: "Clinic Admin/Other",
};

function ProfileRoleSection() {
  const { allowedRoles, role } = useRole();
  const { currentTeam } = useTeam();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editUser] = useMutation(EDIT_USER);

  const isOnTeam = !!currentTeam?.uuid;
  const teamUser = currentTeam?.users?.find(
    (u) => u.uuid === user?.uuid,
  );
  const teamRoleLabel = teamUser
    ? mapRoleIdToRole(teamUser.roleId)
    : null;

  // Team members: show read-only team role
  if (isOnTeam && teamRoleLabel) {
    return (
      <div className="flex items-center gap-sm">
        <span className="text-primary">{teamRoleLabel}</span>
        <DSLinkButton
          dataTestId="profile-role-team-settings-link"
          onClick={() => navigate("/teams")}
        >
          Manage in team settings
        </DSLinkButton>
      </div>
    );
  }

  // Solo users: editable role
  const roleOptions = Object.entries(allowedRoles)
    .filter(([name]) => name in ROLE_LABELS)
    .map(([name, value]) => ({
      name,
      value,
      label: ROLE_LABELS[name],
    }));

  const currentRoleLabel = role?.name
    ? ROLE_LABELS[role.name] ||
      ROLE_LABELS[role.name.toUpperCase()] ||
      role.name
    : "Not set";

  const handleSave = async () => {
    if (!selectedRoleId) return;
    setIsSaving(true);
    try {
      await editUser({
        variables: { roleId: Number(selectedRoleId) },
        refetchQueries: ["getRole"],
      });
      setIsEditing(false);
      alert("success", "Role updated successfully.");
    } catch (err) {
      mixpanel.track("Update Role Failed", {
        error: err.message || err,
      });
      alert("error", err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex flex-col md:flex-row md:items-center gap-sm">
        <DSDropdown
          options={roleOptions}
          value={selectedRoleId}
          onChange={(value) => setSelectedRoleId(value)}
          placeholder="Select a role"
          disabled={isSaving}
          dataTestId="profile-role-select"
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
          size="sm"
          width="w-56"
        />
        <div className="flex gap-sm">
          <DSButton
            type="button"
            variant="cancel"
            size="sm"
            disabled={isSaving}
            onClick={() => setIsEditing(false)}
            dataTestId="profile-role-cancel-button"
          >
            Cancel
          </DSButton>
          <DSButton
            type="button"
            variant="primary"
            size="sm"
            disabled={isSaving || !selectedRoleId}
            onClick={handleSave}
            dataTestId="profile-role-save-button"
          >
            Save
          </DSButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-sm">
      <span className="text-primary">{currentRoleLabel}</span>
      <DSLinkButton
        dataTestId="profile-update-role-button"
        onClick={() => {
          setIsEditing(true);
          setSelectedRoleId(role?.id || null);
        }}
      >
        Update
      </DSLinkButton>
    </div>
  );
}

function ProfileNameSection({ user }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedLastName, setEditedLastName] = useState("");
  const [editUser] = useMutation(EDIT_USER);

  const onSaveClicked = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await editUser({
        variables: {
          firstName: editedFirstName,
          lastName: editedLastName,
        },
      });
      setIsEditing(false);

      alert("success", "Account updated successfully.");
    } catch (err) {
      mixpanel.track("Update Name Failed", {
        error: err.message || err,
      });
      alert("error", err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    const isValid =
      editedFirstName.length > 0 && editedLastName.length > 0;
    return (
      <form
        className="flex flex-col md:flex-row md:items-end gap-sm"
        onSubmit={onSaveClicked}
      >
        <DSInput
          type="text"
          placeholder="First name"
          maxLength={100}
          value={editedFirstName}
          onChange={(e) => setEditedFirstName(e.target.value)}
          disabled={isSaving}
          dataTestId="profile-first-name-input"
        />
        <DSInput
          type="text"
          placeholder="Last name"
          maxLength={100}
          value={editedLastName}
          onChange={(e) => setEditedLastName(e.target.value)}
          disabled={isSaving}
          dataTestId="profile-last-name-input"
        />
        <div className="flex gap-sm">
          <DSButton
            type="button"
            variant="cancel"
            size="sm"
            disabled={isSaving}
            onClick={() => setIsEditing(false)}
            dataTestId="profile-cancel-button"
          >
            Cancel
          </DSButton>
          <DSButton
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSaving || !isValid}
            dataTestId="profile-save-button"
          >
            Save
          </DSButton>
        </div>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-sm">
      <span className="text-primary">
        {user?.firstName} {user?.lastName}
      </span>
      <DSLinkButton
        dataTestId="profile-update-name-button"
        onClick={() => {
          setIsEditing(true);
          setEditedFirstName(user?.firstName || "");
          setEditedLastName(user?.lastName || "");
        }}
      >
        Update
      </DSLinkButton>
    </div>
  );
}
